/**
 * Master destination dataset for the MUNO travel platform.
 *
 * Every destination card, Discover filter, AI suggestion, and /trip/:slug page
 * pulls from this single constant. Categories drive filtering; slugs drive routing.
 *
 * Slug format: [city-lowercase]-[primary-category]
 * Route:       /trip/:slug
 */

const DESTINATIONS = [
    // ═══════════════════════ BEACH ═══════════════════════
    {
        slug: 'maldives-beach',
        name: 'Maldives',
        country: 'Maldives',
        region: 'Asia',
        tagline: 'Turquoise infinity, pure bliss',
        price: 2200,
        match: 95,
        categories: ['beach', 'romance', 'luxury'],
        moods: ['beach', 'romance', 'wellness'],
        image: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=800&q=80',
        bestMonths: 'Nov–Apr',
        flightTime: '~16h from NYC',
        knownFor: ['Overwater villas', 'Snorkeling', 'Coral reefs', 'Private islands', 'Whale sharks'],
        highlights: [
            { title: 'Overwater Bungalow Stay', description: 'Wake up above crystal-clear lagoons with glass-floor villas.' },
            { title: 'Manta Ray Night Dive', description: 'Swim alongside manta rays illuminated by underwater lights.' },
            { title: 'Sandbank Picnic', description: 'Private dining on a tiny sandbank surrounded by turquoise water.' },
        ],
        travelTips: [
            'Book a seaplane transfer for the best aerial views of the atolls.',
            'Visit between November and April for the driest weather.',
            'Many resorts are all-inclusive — compare packages before booking.',
        ],
    },
    {
        slug: 'bali-beach',
        name: 'Bali',
        country: 'Indonesia',
        region: 'Asia',
        tagline: 'Rice terraces & temple sunsets',
        price: 890,
        match: 96,
        categories: ['beach', 'culture', 'adventure', 'romance', 'budget'],
        moods: ['beach', 'wellness', 'nature', 'adventure', 'culture', 'romance'],
        image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=800&q=80',
        bestMonths: 'May–Sep',
        flightTime: '~20h from NYC',
        knownFor: ['Tegallalang terraces', 'Ubud art scene', 'Surf breaks', 'Temple ceremonies', 'Affordable luxury'],
        highlights: [
            { title: 'Tegallalang Rice Terraces', description: 'Walk through iconic emerald-green rice paddies in Ubud.' },
            { title: 'Uluwatu Sunset & Kecak Dance', description: 'Clifftop temple with a mesmerizing fire dance at golden hour.' },
            { title: 'Mount Batur Sunrise Trek', description: 'Pre-dawn hike to a volcanic summit for panoramic sunrise views.' },
        ],
        travelTips: [
            'Rent a scooter for flexibility — traffic in Seminyak/Canggu can be heavy.',
            'The dry season (May–Sep) is ideal for outdoor activities.',
            'Ubud is calmer and more cultural; south Bali is livelier and beachier.',
        ],
    },
    {
        slug: 'santorini-beach',
        name: 'Santorini',
        country: 'Greece',
        region: 'Europe',
        tagline: 'Cliffs, wine & golden light',
        price: 1200,
        match: 94,
        categories: ['beach', 'romance'],
        moods: ['beach', 'romance', 'food'],
        image: 'https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?w=800&q=80',
        bestMonths: 'May–Oct',
        flightTime: '~11h from NYC',
        knownFor: ['Oia sunsets', 'Caldera views', 'White-washed villages', 'Volcanic beaches', 'Assyrtiko wine'],
        highlights: [
            { title: 'Oia Sunset Walk', description: 'Stroll to the castle ruins for the world\'s most photographed sunset.' },
            { title: 'Red Beach Swim', description: 'Volcanic red cliffs plunging into crystal-clear Aegean waters.' },
            { title: 'Santo Wines Tasting', description: 'Sip local Assyrtiko white wine overlooking the caldera.' },
        ],
        travelTips: [
            'Book Oia accommodation months in advance — it sells out fast in summer.',
            'Take the public bus instead of taxis to save significantly.',
            'Visit in May or October for warm weather without peak-season crowds.',
        ],
    },
    {
        slug: 'tulum-beach',
        name: 'Tulum',
        country: 'Mexico',
        region: 'Latin America',
        tagline: 'Ruins meet the Caribbean',
        price: 680,
        match: 91,
        categories: ['beach', 'budget'],
        moods: ['beach', 'wellness', 'adventure', 'nature'],
        image: 'https://images.unsplash.com/photo-1504730030853-eff311f57d3c?w=800&q=80',
        bestMonths: 'Nov–Apr',
        flightTime: '~4h from NYC',
        knownFor: ['Mayan ruins', 'Cenotes', 'Boho beach clubs', 'Eco hotels'],
        highlights: [
            { title: 'Tulum Archaeological Zone', description: 'Clifftop Mayan ruins overlooking the Caribbean Sea.' },
            { title: 'Gran Cenote Swim', description: 'Crystal-clear underground swimming in a limestone sinkhole.' },
            { title: 'Beach Road Cycling', description: 'Pedal past bohemian boutiques, cafés and jungle hotels.' },
        ],
        travelTips: [
            'Rent a bicycle — the beach strip is car-free and bikes are the norm.',
            'Bring cash; many beach clubs don\'t accept cards.',
            'Visit cenotes early morning to beat the crowds.',
        ],
    },
    {
        slug: 'amalfi-coast-beach',
        name: 'Amalfi Coast',
        country: 'Italy',
        region: 'Europe',
        tagline: 'Cliffside lemons & azure sea',
        price: 1400,
        match: 93,
        categories: ['beach', 'romance', 'food'],
        moods: ['beach', 'romance', 'food'],
        image: 'https://images.unsplash.com/photo-1533587851505-d119e13fa0d7?w=800&q=80',
        bestMonths: 'May–Sep',
        flightTime: '~9h from NYC',
        knownFor: ['Positano cliffs', 'Path of the Gods', 'Limoncello', 'Coastal drives', 'Ravello concerts'],
        highlights: [
            { title: 'Path of the Gods Hike', description: 'Breathtaking coastal trail between Agerola and Positano.' },
            { title: 'Positano Beach Day', description: 'Colorful cliffside village with pebble beaches and pastel houses.' },
            { title: 'Ravello Villa Gardens', description: 'Elegant hilltop gardens with panoramic views and classical concerts.' },
        ],
        travelTips: [
            'Use SITA buses or ferries — driving the coastal road is stressful.',
            'Book restaurants in advance during July and August.',
            'Stay in Praiano for lower prices and less crowds than Positano.',
        ],
    },
    {
        slug: 'phuket-beach',
        name: 'Phuket',
        country: 'Thailand',
        region: 'Asia',
        tagline: 'Tropical shores, vibrant nights',
        price: 720,
        match: 90,
        categories: ['beach', 'budget'],
        moods: ['beach', 'nightlife', 'adventure', 'family'],
        image: 'https://images.unsplash.com/photo-1589394815804-964ed0be2eb5?w=800&q=80',
        bestMonths: 'Nov–Mar',
        flightTime: '~20h from NYC',
        knownFor: ['Phi Phi islands', 'Patong nightlife', 'Thai massage', 'Island hopping', 'Street food'],
        highlights: [
            { title: 'Phi Phi Island Day Trip', description: 'Snorkel in Maya Bay and explore limestone karst islands.' },
            { title: 'Old Phuket Town Walk', description: 'Sino-Portuguese shophouses, street art and local cafés.' },
            { title: 'Big Buddha Viewpoint', description: '45m marble Buddha statue with panoramic island views.' },
        ],
        travelTips: [
            'Stay in Kata or Karon for a quieter alternative to Patong.',
            'Long-tail boat tours are cheaper than speedboat tours.',
            'The rainy season (May–Oct) still has plenty of sunny days.',
        ],
    },
    {
        slug: 'cancun-beach',
        name: 'Cancún',
        country: 'Mexico',
        region: 'Latin America',
        tagline: 'White sand & Caribbean blue',
        price: 580,
        match: 89,
        categories: ['beach'],
        moods: ['beach', 'nightlife', 'family', 'adventure'],
        image: 'https://images.unsplash.com/photo-1552074284-5e88ef1aef18?w=800&q=80',
        bestMonths: 'Dec–Apr',
        flightTime: '~4h from NYC',
        knownFor: ['Hotel Zone beaches', 'Chichén Itzá', 'Cenotes', 'Spring break scene', 'All-inclusive resorts'],
        highlights: [
            { title: 'Chichén Itzá Day Trip', description: 'One of the New Seven Wonders of the World, 2 hours from Cancún.' },
            { title: 'Isla Mujeres Ferry', description: 'Laid-back island with golf-cart exploring and snorkeling at MUSA.' },
            { title: 'Xcaret Eco-Park', description: 'Underground rivers, snorkeling, and an evening cultural show.' },
        ],
        travelTips: [
            'The Hotel Zone has beaches; downtown has authentic food and lower prices.',
            'Book Chichén Itzá tickets online to skip queues.',
            'Hurricane season is June–November — check forecasts before booking.',
        ],
    },
    {
        slug: 'zanzibar-beach',
        name: 'Zanzibar',
        country: 'Tanzania',
        region: 'Africa',
        tagline: 'Spice islands & turquoise tides',
        price: 950,
        match: 88,
        categories: ['beach'],
        moods: ['beach', 'culture', 'adventure'],
        image: 'https://images.unsplash.com/photo-1586861635167-e5223aadc9fe?w=800&q=80',
        bestMonths: 'Jun–Oct',
        flightTime: '~18h from NYC',
        knownFor: ['Stone Town', 'Spice farms', 'Dhow sailing', 'Nungwi beach', 'Jozani forest'],
        highlights: [
            { title: 'Stone Town Walking Tour', description: 'UNESCO heritage streets with carved doors, bazaars and rooftop cafés.' },
            { title: 'Spice Farm Visit', description: 'See cloves, vanilla and cinnamon growing in lush tropical gardens.' },
            { title: 'Nungwi Beach Sunset', description: 'Pristine white sand with traditional dhow boats and no crowds.' },
        ],
        travelTips: [
            'Book a spice tour through a reputable local guide.',
            'Dress modestly in Stone Town — Zanzibar is predominantly Muslim.',
            'The east coast has lower tides; the north has the best swimming beaches.',
        ],
    },
    {
        slug: 'hawaii-beach',
        name: 'Hawaii',
        country: 'USA',
        region: 'USA',
        tagline: 'Volcanic drama meets aloha',
        price: 1100,
        match: 92,
        categories: ['beach'],
        moods: ['beach', 'nature', 'adventure', 'romance', 'family'],
        image: 'https://images.unsplash.com/photo-1507876466758-bc54f384809c?w=800&q=80',
        bestMonths: 'Apr–Oct',
        flightTime: '~11h from NYC',
        knownFor: ['Na Pali Coast', 'Volcanoes National Park', 'Waikiki Beach', 'Snorkeling', 'Luau feasts'],
        highlights: [
            { title: 'Na Pali Coast Boat Tour', description: 'Towering emerald sea cliffs and hidden beaches on Kauai.' },
            { title: 'Volcanoes National Park', description: 'Walk across lava fields and see Kīlauea\'s crater on Big Island.' },
            { title: 'Snorkeling at Molokini', description: 'Crystal-clear crescent-shaped volcanic crater off Maui\'s coast.' },
        ],
        travelTips: [
            'Each island has a different vibe — Maui is all-round, Kauai is lush, Big Island is adventurous.',
            'Rent a car on every island; public transport is limited.',
            'Book sunrise at Haleakalā weeks in advance — slots sell out.',
        ],
    },
    {
        slug: 'mykonos-beach',
        name: 'Mykonos',
        country: 'Greece',
        region: 'Europe',
        tagline: 'Windmills, waves & white streets',
        price: 1300,
        match: 90,
        categories: ['beach'],
        moods: ['beach', 'nightlife', 'romance'],
        image: 'https://images.unsplash.com/photo-1601581875309-fafbf2d3ed3a?w=800&q=80',
        bestMonths: 'Jun–Sep',
        flightTime: '~11h from NYC',
        knownFor: ['Little Venice', 'Party scene', 'Windmills', 'Beach clubs', 'Delos island'],
        highlights: [
            { title: 'Little Venice Sunset', description: 'Cocktails on the waterfront as waves crash beneath your feet.' },
            { title: 'Delos Island Excursion', description: 'Ancient archaeological sanctuary — birthplace of Apollo.' },
            { title: 'Paradise Beach Club', description: 'World-famous beach party with DJ sets and crystal waters.' },
        ],
        travelTips: [
            'Mykonos is expensive — eat at inland tavernas for better prices.',
            'Visit in early June or late September for fewer crowds.',
            'The Meltemi wind can be strong July–August; north-facing beaches get choppy.',
        ],
    },

    // ═══════════════════════ CULTURE ═══════════════════════
    {
        slug: 'kyoto-culture',
        name: 'Kyoto',
        country: 'Japan',
        region: 'Asia',
        tagline: 'Bamboo groves & golden pavilions',
        price: 1250,
        match: 93,
        categories: ['culture', 'romance', 'food'],
        moods: ['culture', 'wellness', 'nature', 'romance'],
        image: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800&q=80',
        bestMonths: 'Mar–May, Oct–Nov',
        flightTime: '~14h from NYC',
        knownFor: ['Fushimi Inari', 'Bamboo Grove', 'Geisha district', 'Tea ceremonies', 'Cherry blossoms'],
        highlights: [
            { title: 'Fushimi Inari Shrine', description: 'Walk through thousands of vermilion torii gates up the mountain.' },
            { title: 'Arashiyama Bamboo Grove', description: 'Towering bamboo stalks creating an ethereal green corridor.' },
            { title: 'Gion Evening Walk', description: 'Spot geiko and maiko in Kyoto\'s traditional entertainment district.' },
        ],
        travelTips: [
            'Get an IC card (ICOCA) for seamless bus and train travel.',
            'Cherry blossom season (late March–early April) is magical but very busy.',
            'Rent a bicycle — Kyoto is flat and bike-friendly.',
        ],
    },
    {
        slug: 'rome-culture',
        name: 'Rome',
        country: 'Italy',
        region: 'Europe',
        tagline: 'Colosseum, gelato & cobblestone',
        price: 950,
        match: 93,
        categories: ['culture', 'food'],
        moods: ['culture', 'food', 'romance'],
        image: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=800&q=80',
        bestMonths: 'Apr–Jun, Sep–Oct',
        flightTime: '~9h from NYC',
        knownFor: ['Colosseum', 'Vatican Museums', 'Trastevere', 'Roman Forum', 'Pasta & gelato'],
        highlights: [
            { title: 'Colosseum & Roman Forum', description: 'Step into 2,000 years of history at Rome\'s ancient amphitheatre.' },
            { title: 'Vatican & Sistine Chapel', description: 'Michelangelo\'s masterpiece ceiling in the world\'s smallest country.' },
            { title: 'Trastevere Evening', description: 'Cobblestone lanes, vine-covered trattorias and live street music.' },
        ],
        travelTips: [
            'Book Vatican tickets online — the queue can be 3+ hours.',
            'Drink from the nasoni (public fountains) — the water is clean and cold.',
            'Avoid restaurants right next to major landmarks; walk one block for better food.',
        ],
    },
    {
        slug: 'athens-culture',
        name: 'Athens',
        country: 'Greece',
        region: 'Europe',
        tagline: 'Acropolis views & rooftop wine',
        price: 650,
        match: 90,
        categories: ['culture', 'budget'],
        moods: ['culture', 'food', 'nightlife'],
        image: 'https://images.unsplash.com/photo-1555993539-1732b0258235?w=800&q=80',
        bestMonths: 'Apr–Jun, Sep–Oct',
        flightTime: '~10h from NYC',
        knownFor: ['Acropolis', 'Plaka district', 'Souvlaki', 'Rooftop bars', 'Island-hopping gateway'],
        highlights: [
            { title: 'Acropolis & Parthenon', description: 'The crown of Western civilization on its ancient hilltop.' },
            { title: 'Plaka Neighborhood', description: 'Pastel neoclassical houses, tavernas and cobblestone streets below the Acropolis.' },
            { title: 'Cape Sounion Sunset', description: 'Temple of Poseidon perched on dramatic cliffs above the Aegean.' },
        ],
        travelTips: [
            'Buy the combined archaeological sites ticket — it covers 7 sites and saves money.',
            'Monastiraki flea market is great for unique souvenirs.',
            'Athens is a perfect base for island-hopping via Piraeus port.',
        ],
    },
    {
        slug: 'istanbul-culture',
        name: 'Istanbul',
        country: 'Turkey',
        region: 'Europe',
        tagline: 'Minarets, bazaars & two continents',
        price: 700,
        match: 92,
        categories: ['culture', 'food'],
        moods: ['culture', 'food', 'shopping'],
        image: 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=800&q=80',
        bestMonths: 'Apr–Jun, Sep–Nov',
        flightTime: '~10h from NYC',
        knownFor: ['Hagia Sophia', 'Grand Bazaar', 'Bosphorus cruise', 'Turkish breakfast', 'Hammams'],
        highlights: [
            { title: 'Hagia Sophia', description: '1,500-year-old architectural wonder spanning empires and religions.' },
            { title: 'Grand Bazaar', description: 'One of the world\'s oldest covered markets with 4,000+ shops.' },
            { title: 'Bosphorus Sunset Cruise', description: 'Sail between Europe and Asia as the sky turns gold over the strait.' },
        ],
        travelTips: [
            'Get an Istanbul Museum Pass for skip-the-line access to top attractions.',
            'Always haggle at the Grand Bazaar — it\'s expected and part of the culture.',
            'Try a traditional Turkish breakfast — it\'s a feast of small plates.',
        ],
    },
    {
        slug: 'marrakech-culture',
        name: 'Marrakech',
        country: 'Morocco',
        region: 'Africa',
        tagline: 'Souks, spice & starlit riads',
        price: 520,
        match: 91,
        categories: ['culture', 'budget'],
        moods: ['culture', 'food', 'shopping', 'romance'],
        image: 'https://images.unsplash.com/photo-1597212618440-806262de4f6b?w=800&q=80',
        bestMonths: 'Mar–May, Sep–Nov',
        flightTime: '~8h from NYC',
        knownFor: ['Jemaa el-Fna', 'Riads', 'Atlas day trips', 'Leather tanneries', 'Tagine & mint tea'],
        highlights: [
            { title: 'Jemaa el-Fna Square', description: 'Snake charmers, storytellers and sizzling food stalls at dusk.' },
            { title: 'Majorelle Garden', description: 'Yves Saint Laurent\'s electric-blue botanical garden oasis.' },
            { title: 'Atlas Mountains Day Trip', description: 'Berber villages and waterfalls just 90 minutes from the medina.' },
        ],
        travelTips: [
            'Stay in a riad for an authentic experience — they\'re often cheaper than hotels.',
            'Negotiate prices confidently; initial quotes are typically 3x the fair price.',
            'Summers are extremely hot (40°C+); spring and autumn are much more comfortable.',
        ],
    },
    {
        slug: 'prague-culture',
        name: 'Prague',
        country: 'Czech Republic',
        region: 'Europe',
        tagline: 'Gothic spires & golden beer',
        price: 550,
        match: 89,
        categories: ['culture', 'budget'],
        moods: ['culture', 'nightlife', 'food'],
        image: 'https://images.unsplash.com/photo-1519677100203-a0e668c92439?w=800&q=80',
        bestMonths: 'Apr–Jun, Sep–Oct',
        flightTime: '~9h from NYC',
        knownFor: ['Charles Bridge', 'Old Town Square', 'Beer gardens', 'Prague Castle', 'Art Nouveau'],
        highlights: [
            { title: 'Charles Bridge at Dawn', description: 'Walk the 14th-century bridge before the crowds arrive.' },
            { title: 'Prague Castle Complex', description: 'The world\'s largest ancient castle with St. Vitus Cathedral.' },
            { title: 'Beer Tasting Crawl', description: 'Czech Republic has the highest beer consumption per capita — taste why.' },
        ],
        travelTips: [
            'Exchange money at banks, never at street kiosks — rates are terrible.',
            'Prague is very walkable; most attractions are within 30 minutes on foot.',
            'Check for free walking tours — tip-based tours are excellent here.',
        ],
    },
    {
        slug: 'vienna-culture',
        name: 'Vienna',
        country: 'Austria',
        region: 'Europe',
        tagline: 'Palaces, waltzes & Sachertorte',
        price: 850,
        match: 90,
        categories: ['culture'],
        moods: ['culture', 'food', 'romance'],
        image: 'https://images.unsplash.com/photo-1516550893923-42d28e5677af?w=800&q=80',
        bestMonths: 'Apr–Jun, Sep–Oct',
        flightTime: '~9h from NYC',
        knownFor: ['Schönbrunn Palace', 'Vienna State Opera', 'Coffee houses', 'Museums Quarter', 'Christmas markets'],
        highlights: [
            { title: 'Schönbrunn Palace', description: 'Habsburg imperial summer residence with stunning gardens.' },
            { title: 'Vienna State Opera', description: 'Standing-room tickets from €4 for world-class performances.' },
            { title: 'Naschmarkt', description: 'Vienna\'s biggest outdoor market — olive stalls, falafel and vintage finds.' },
        ],
        travelTips: [
            'Standing-room opera tickets sell out fast — queue 90 minutes before.',
            'The Vienna City Card covers transit and gives museum discounts.',
            'Christmas markets (mid-Nov to Dec) are magical — visit Rathausplatz.',
        ],
    },
    {
        slug: 'havana-culture',
        name: 'Havana',
        country: 'Cuba',
        region: 'Latin America',
        tagline: 'Classic cars & salsa rhythms',
        price: 600,
        match: 88,
        categories: ['culture'],
        moods: ['culture', 'nightlife', 'adventure'],
        image: 'https://images.unsplash.com/photo-1500759285222-a95626b934cb?w=800&q=80',
        bestMonths: 'Nov–Apr',
        flightTime: '~3.5h from Miami',
        knownFor: ['Old Havana', 'Vintage cars', 'Live salsa', 'Malecón', 'Rum & cigars'],
        highlights: [
            { title: 'Old Havana Walking Tour', description: 'UNESCO colonial architecture, plazas and crumbling pastel facades.' },
            { title: 'Classic Car Ride', description: 'Cruise the Malecón waterfront in a 1950s Chevrolet convertible.' },
            { title: 'Fábrica de Arte', description: 'Havana\'s coolest art space — galleries, live music and rooftop bar.' },
        ],
        travelTips: [
            'Bring cash (USD or EUR) — ATMs are unreliable and credit cards rarely work.',
            'Book casa particulares (private homestays) for authentic local experience.',
            'Internet is limited; buy ETECSA Wi-Fi cards at hotels or parks.',
        ],
    },
    {
        slug: 'florence-culture',
        name: 'Florence',
        country: 'Italy',
        region: 'Europe',
        tagline: 'Renaissance art & Tuscan hills',
        price: 900,
        match: 91,
        categories: ['culture', 'food'],
        moods: ['culture', 'food', 'romance'],
        image: 'https://images.unsplash.com/photo-1543429776-2782fc8e10dd?w=800&q=80',
        bestMonths: 'Apr–Jun, Sep–Oct',
        flightTime: '~9h from NYC',
        knownFor: ['Uffizi Gallery', 'Duomo', 'Ponte Vecchio', 'Tuscan wine', 'Leather markets'],
        highlights: [
            { title: 'Uffizi Gallery', description: 'Botticelli\'s Birth of Venus and the finest Renaissance collection.' },
            { title: 'Climb the Duomo', description: '463 steps to the top of Brunelleschi\'s dome for city-wide views.' },
            { title: 'San Lorenzo Market', description: 'Leather goods, Florentine lampredotto and truffle pasta at the central market.' },
        ],
        travelTips: [
            'Book Uffizi tickets online — walk-up queues can exceed 2 hours.',
            'Florence is compact; you can walk everywhere in the historic center.',
            'Try a bistecca alla fiorentina — the city\'s signature T-bone steak.',
        ],
    },
    {
        slug: 'lisbon-culture',
        name: 'Lisbon',
        country: 'Portugal',
        region: 'Europe',
        tagline: 'Tiled walls & pastel sunsets',
        price: 680,
        match: 92,
        categories: ['culture', 'food', 'budget'],
        moods: ['culture', 'food', 'nightlife', 'beach'],
        image: 'https://images.unsplash.com/photo-1585208798174-6cedd86e019a?w=800&q=80',
        bestMonths: 'Mar–Oct',
        flightTime: '~7h from NYC',
        knownFor: ['Tram 28', 'Pastéis de Belém', 'Alfama district', 'Fado music', 'Azulejo tiles'],
        highlights: [
            { title: 'Tram 28 Ride', description: 'Vintage yellow tram weaving through the city\'s seven hills.' },
            { title: 'Alfama & Fado Night', description: 'Get lost in medieval alleys, then hear mournful fado in a tiny tavern.' },
            { title: 'Belém Tower & Pastéis', description: 'UNESCO waterfront tower followed by the original custard tarts since 1837.' },
        ],
        travelTips: [
            'Wear comfortable shoes — Lisbon is hilly with cobblestoned streets.',
            'The Lisboa Card covers transit, attractions, and discounts.',
            'Bairro Alto is the nightlife district — things don\'t start until 11pm.',
        ],
    },

    // ═══════════════════════ ADVENTURE ═══════════════════════
    {
        slug: 'iceland-adventure',
        name: 'Iceland',
        country: 'Iceland',
        region: 'Europe',
        tagline: 'Northern lights & black sand',
        price: 1400,
        match: 93,
        categories: ['adventure'],
        moods: ['nature', 'adventure'],
        image: 'https://images.unsplash.com/photo-1504829857797-ddff29c27927?w=800&q=80',
        bestMonths: 'Jun–Aug (midnight sun), Sep–Mar (northern lights)',
        flightTime: '~5h from NYC',
        knownFor: ['Northern Lights', 'Blue Lagoon', 'Golden Circle', 'Glaciers', 'Waterfalls'],
        highlights: [
            { title: 'Golden Circle Drive', description: 'Geysir, Gullfoss waterfall and Þingvellir tectonic plates in one epic loop.' },
            { title: 'Glacier Hike', description: 'Strap on crampons and walk atop Sólheimajökull glacier.' },
            { title: 'Northern Lights Hunt', description: 'Chase the aurora borealis across dark volcanic landscapes.' },
        ],
        travelTips: [
            'Rent a 4WD if visiting in winter or going to the highlands.',
            'Layer up — weather changes every 15 minutes, even in summer.',
            'Book the Blue Lagoon in advance; walk-ups are rarely available.',
        ],
    },
    {
        slug: 'new-zealand-adventure',
        name: 'New Zealand',
        country: 'New Zealand',
        region: 'Oceania',
        tagline: 'Fjords, peaks & Hobbit holes',
        price: 1600,
        match: 94,
        categories: ['adventure'],
        moods: ['nature', 'adventure'],
        image: 'https://images.unsplash.com/photo-1469521669194-babb45599def?w=800&q=80',
        bestMonths: 'Dec–Mar',
        flightTime: '~18h from LAX',
        knownFor: ['Milford Sound', 'Queenstown bungee', 'Hobbiton', 'Tongariro Crossing', 'Glaciers'],
        highlights: [
            { title: 'Milford Sound Cruise', description: 'Towering fjord walls, waterfalls and dolphins in Fiordland.' },
            { title: 'Tongariro Alpine Crossing', description: 'One-day volcanic trek through emerald lakes and red craters.' },
            { title: 'Queenstown Adventure Hub', description: 'Bungee, skydive, jet-boat — the adrenaline capital of the world.' },
        ],
        travelTips: [
            'Rent a campervan for ultimate flexibility on the South Island.',
            'Book Great Walk huts months in advance through DOC.',
            'Drive times are longer than the map suggests — roads are winding.',
        ],
    },
    {
        slug: 'patagonia-adventure',
        name: 'Patagonia',
        country: 'Argentina / Chile',
        region: 'Latin America',
        tagline: 'Glaciers, peaks & endless wind',
        price: 1500,
        match: 91,
        categories: ['adventure'],
        moods: ['nature', 'adventure'],
        image: 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=800&q=80',
        bestMonths: 'Nov–Mar',
        flightTime: '~14h from NYC to Buenos Aires + domestic',
        knownFor: ['Torres del Paine', 'Perito Moreno', 'El Chaltén', 'Penguins', 'Gaucho culture'],
        highlights: [
            { title: 'Torres del Paine W Trek', description: '4–5 day circuit past glaciers, lakes and the iconic granite towers.' },
            { title: 'Perito Moreno Glacier', description: 'Watch massive ice chunks calve into Lake Argentino.' },
            { title: 'El Chaltén Day Hike', description: 'Trek to Laguna de los Tres for the best view of Mount Fitz Roy.' },
        ],
        travelTips: [
            'Book refugios on the W Trek well in advance — they sell out months ahead.',
            'Wind is relentless; bring layers and a good windbreaker.',
            'El Calafate is the gateway city; fly from Buenos Aires.',
        ],
    },
    {
        slug: 'nepal-adventure',
        name: 'Nepal',
        country: 'Nepal',
        region: 'Asia',
        tagline: 'Himalayan peaks & prayer flags',
        price: 800,
        match: 90,
        categories: ['adventure'],
        moods: ['nature', 'adventure', 'culture'],
        image: 'https://images.unsplash.com/photo-1544735716-392fe2489ffa?w=800&q=80',
        bestMonths: 'Oct–Nov, Mar–Apr',
        flightTime: '~16h from NYC',
        knownFor: ['Everest Base Camp', 'Annapurna Circuit', 'Kathmandu temples', 'Pokhara', 'Tiger\'s Nest proximity'],
        highlights: [
            { title: 'Everest Base Camp Trek', description: '12-day trek through Sherpa villages to the foot of the world\'s highest peak.' },
            { title: 'Pokhara Lakeside', description: 'Annapurna views reflected in Phewa Lake, paragliding and peace.' },
            { title: 'Boudhanath Stupa', description: 'One of the largest Buddhist stupas in the world, circled by prayer wheels.' },
        ],
        travelTips: [
            'Hire a local guide for treks — it supports the economy and is safer.',
            'Altitude sickness is real; acclimatize properly on EBC trek.',
            'Kathmandu can be overwhelming — escape to Bhaktapur for a calmer experience.',
        ],
    },
    {
        slug: 'costa-rica-adventure',
        name: 'Costa Rica',
        country: 'Costa Rica',
        region: 'Latin America',
        tagline: 'Jungle canopy & volcanic springs',
        price: 720,
        match: 91,
        categories: ['adventure'],
        moods: ['adventure', 'nature', 'family', 'wellness'],
        image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&q=80',
        bestMonths: 'Dec–Apr',
        flightTime: '~5h from NYC',
        knownFor: ['Arenal volcano', 'Cloud forests', 'Zip-lining', 'Wildlife', 'Pura vida lifestyle'],
        highlights: [
            { title: 'Arenal Hot Springs', description: 'Soak in natural volcanic hot springs with jungle views.' },
            { title: 'Monteverde Cloud Forest', description: 'Suspension bridges through misty treetops full of toucans and quetzals.' },
            { title: 'Manuel Antonio Beach', description: 'National park where monkeys roam the beach and jungle meets the sea.' },
        ],
        travelTips: [
            'Rent a 4WD — many roads to beaches are unpaved.',
            'The "green season" (May–Nov) has afternoon rain but lush scenery and lower prices.',
            'Carry small bills; rural areas rarely have card machines.',
        ],
    },
    {
        slug: 'norwegian-fjords-adventure',
        name: 'Norwegian Fjords',
        country: 'Norway',
        region: 'Europe',
        tagline: 'Deep blue fjords, midnight sun',
        price: 1350,
        match: 89,
        categories: ['adventure'],
        moods: ['nature', 'adventure'],
        image: 'https://images.unsplash.com/photo-1520769669658-f07657f5a307?w=800&q=80',
        bestMonths: 'May–Sep',
        flightTime: '~8h from NYC',
        knownFor: ['Geirangerfjord', 'Trolltunga', 'Bergen', 'Midnight sun', 'Flåm Railway'],
        highlights: [
            { title: 'Geirangerfjord Cruise', description: 'UNESCO fjord with thundering waterfalls and emerald water.' },
            { title: 'Trolltunga Hike', description: '10-hour round-trip to the famous rock tongue jutting over a lake.' },
            { title: 'Bergen Old Town', description: 'Colorful Bryggen wharf, fish market and funicular views.' },
        ],
        travelTips: [
            'Norway is expensive; self-cater and camp to save money.',
            'Book Trolltunga early; the season is short (Jun–Sep).',
            'The Flåm Railway is one of the world\'s most scenic train rides.',
        ],
    },
    {
        slug: 'borneo-adventure',
        name: 'Borneo',
        country: 'Malaysia',
        region: 'Asia',
        tagline: 'Rainforest depths & orangutans',
        price: 1000,
        match: 88,
        categories: ['adventure'],
        moods: ['nature', 'adventure'],
        image: 'https://images.unsplash.com/photo-1580674287404-46e09bf1d744?w=800&q=80',
        bestMonths: 'Mar–Oct',
        flightTime: '~22h from NYC',
        knownFor: ['Orangutans', 'Mount Kinabalu', 'Sipadan diving', 'Rainforest canopy', 'Proboscis monkeys'],
        highlights: [
            { title: 'Sepilok Orangutan Centre', description: 'Watch semi-wild orangutans swing to feeding platforms in the jungle.' },
            { title: 'Mount Kinabalu Climb', description: 'Southeast Asia\'s highest peak — a challenging 2-day summit.' },
            { title: 'Sipadan Island Diving', description: 'World-class wall diving with barracuda tornadoes and sea turtles.' },
        ],
        travelTips: [
            'Sipadan dive permits are limited — book months in advance.',
            'Apply insect repellent generously; the jungle is full of mosquitoes.',
            'Sabah (Malaysian Borneo) is the most accessible part for tourists.',
        ],
    },
    {
        slug: 'swiss-alps-adventure',
        name: 'Swiss Alps',
        country: 'Switzerland',
        region: 'Europe',
        tagline: 'Alpine peaks & chocolate villages',
        price: 1500,
        match: 92,
        categories: ['adventure'],
        moods: ['nature', 'adventure', 'romance'],
        image: 'https://images.unsplash.com/photo-1530122037265-a5f1f91d3b99?w=800&q=80',
        bestMonths: 'Jun–Sep (hiking), Dec–Mar (skiing)',
        flightTime: '~8h from NYC',
        knownFor: ['Matterhorn', 'Jungfraujoch', 'Interlaken', 'Glacier Express', 'Swiss chocolate'],
        highlights: [
            { title: 'Jungfraujoch Railway', description: 'Train to the "Top of Europe" at 3,454m with panoramic Alpine views.' },
            { title: 'Grindelwald First Cliff Walk', description: 'Glass-bottomed walkway jutting over a sheer Alpine cliff face.' },
            { title: 'Glacier Express', description: '8-hour scenic train ride from Zermatt to St. Moritz through 291 bridges.' },
        ],
        travelTips: [
            'Get a Swiss Travel Pass — it covers trains, buses, boats and mountain transport.',
            'Switzerland is very expensive; buy groceries at Coop or Migros.',
            'Book mountain excursions for clear mornings — clouds build by afternoon.',
        ],
    },

    // ═══════════════════════ FOOD ═══════════════════════
    {
        slug: 'bangkok-food',
        name: 'Bangkok',
        country: 'Thailand',
        region: 'Asia',
        tagline: 'Street food capital of earth',
        price: 550,
        match: 94,
        categories: ['food', 'budget', 'city'],
        moods: ['food', 'nightlife', 'culture', 'shopping'],
        image: 'https://images.unsplash.com/photo-1508009603885-50cf7c579365?w=800&q=80',
        bestMonths: 'Nov–Feb',
        flightTime: '~18h from NYC',
        knownFor: ['Street food', 'Temples', 'Night markets', 'Rooftop bars', 'Floating markets'],
        highlights: [
            { title: 'Chinatown Street Food', description: 'Yaowarat Road explodes with pad thai, mango sticky rice and grilled satay at dusk.' },
            { title: 'Grand Palace & Wat Pho', description: 'Glittering temple complex and the giant reclining Buddha.' },
            { title: 'Rooftop Cocktails', description: 'Sky-high sunset drinks at Lebua or Vertigo with panoramic city views.' },
        ],
        travelTips: [
            'Eat where locals eat — street stalls with queues are the safest and tastiest.',
            'Use the BTS Skytrain and MRT to avoid notorious Bangkok traffic.',
            'November–February is cool season (by Thai standards) and most pleasant.',
        ],
    },
    {
        slug: 'tokyo-food',
        name: 'Tokyo',
        country: 'Japan',
        region: 'Asia',
        tagline: 'Neon lights & Michelin magic',
        price: 1350,
        match: 96,
        categories: ['food', 'culture', 'city'],
        moods: ['culture', 'food', 'nightlife', 'shopping', 'adventure'],
        image: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=800&q=80',
        bestMonths: 'Mar–May, Sep–Nov',
        flightTime: '~14h from NYC',
        knownFor: ['Sushi & ramen', 'Tsukiji Market', 'Izakaya culture', 'Michelin stars', 'Depachika food halls'],
        highlights: [
            { title: 'Tsukiji Outer Market', description: 'Fresh sushi, tamagoyaki and matcha treats steps from the fish market.' },
            { title: 'Izakaya Hopping in Yurakucho', description: 'Tiny yakitori joints under the train tracks, filled with salary workers and smoke.' },
            { title: 'Ramen Alley in Shinjuku', description: 'Choose your broth, firmness and toppings at vending-machine ramen shops.' },
        ],
        travelTips: [
            'Tokyo has more Michelin stars than any city on Earth — many are affordable.',
            'Convenience store food (7-Eleven, Lawson) is genuinely excellent.',
            'Tipping is not customary and can be considered rude.',
        ],
    },
    {
        slug: 'barcelona-food',
        name: 'Barcelona',
        country: 'Spain',
        region: 'Europe',
        tagline: 'Tapas, cava & Mediterranean soul',
        price: 890,
        match: 92,
        categories: ['food', 'beach', 'culture', 'city'],
        moods: ['beach', 'culture', 'food', 'nightlife'],
        image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=80',
        bestMonths: 'May–Jun, Sep',
        flightTime: '~8h from NYC',
        knownFor: ['La Boqueria', 'Tapas bars', 'Cava', 'Seafood paella', 'El Born food scene'],
        highlights: [
            { title: 'La Boqueria Market', description: 'Sensory overload — fruit juices, ibérico ham and fresh seafood at Barcelona\'s iconic market.' },
            { title: 'El Born Tapas Crawl', description: 'Hop between hidden bars for patatas bravas, jamón and pintxos.' },
            { title: 'Cooking Class', description: 'Learn to make authentic paella and sangria in a local chef\'s kitchen.' },
        ],
        travelTips: [
            'Spaniards eat late — lunch at 2pm, dinner after 9pm.',
            'Order "vermut" (vermouth) at a bodega before lunch — it\'s a Barcelona tradition.',
            'Skip the tourist paella joints on La Rambla; head to Barceloneta instead.',
        ],
    },
    {
        slug: 'bologna-food',
        name: 'Bologna',
        country: 'Italy',
        region: 'Europe',
        tagline: 'Italy\'s ultimate food city',
        price: 750,
        match: 91,
        categories: ['food'],
        moods: ['food', 'culture'],
        image: 'https://images.unsplash.com/photo-1598948485421-33a1655d3c38?w=800&q=80',
        bestMonths: 'Apr–Jun, Sep–Oct',
        flightTime: '~9h from NYC',
        knownFor: ['Tortellini', 'Ragù bolognese', 'Mortadella', 'Porticos', 'Oldest university'],
        highlights: [
            { title: 'Quadrilatero Market', description: 'Medieval alleys packed with delis, cheese shops and fresh pasta counters.' },
            { title: 'Tortellini-Making Class', description: 'Learn to fold the perfect belly-button-shaped pasta with a nonna.' },
            { title: 'Asinelli Tower Climb', description: '498 steps to a panoramic view of terracotta rooftops and rolling hills.' },
        ],
        travelTips: [
            'Never order "spaghetti bolognese" — ask for tagliatelle al ragù instead.',
            'Bologna is a university city; it\'s lively, affordable and full of aperitivo bars.',
            'Day-trip to Parma or Modena for Parmigiano and balsamic vinegar tours.',
        ],
    },
    {
        slug: 'mexico-city-food',
        name: 'Mexico City',
        country: 'Mexico',
        region: 'Latin America',
        tagline: 'Tacos, murals & mezcal nights',
        price: 500,
        match: 93,
        categories: ['food', 'culture', 'budget', 'city'],
        moods: ['food', 'culture', 'nightlife'],
        image: 'https://images.unsplash.com/photo-1585464231875-d9ef1f5ad396?w=800&q=80',
        bestMonths: 'Oct–May',
        flightTime: '~5h from NYC',
        knownFor: ['Street tacos', 'Frida Kahlo museum', 'Mezcal bars', 'Roma-Condesa', 'Lucha libre'],
        highlights: [
            { title: 'Taco Crawl in Roma', description: 'Al pastor, suadero and tlayuda at legendary taco stands.' },
            { title: 'Xochimilco Floating Gardens', description: 'Colorful trajinera boats through ancient Aztec canals with mariachi bands.' },
            { title: 'Frida Kahlo Museum', description: 'The Blue House — intimate look into the life of Mexico\'s most iconic artist.' },
        ],
        travelTips: [
            'Eat at taco stands with the longest lines — that\'s where the best food is.',
            'Use Uber or the Metrobús; avoid hailing random taxis.',
            'Roma and Condesa neighborhoods have the best food and nightlife.',
        ],
    },
    {
        slug: 'naples-food',
        name: 'Naples',
        country: 'Italy',
        region: 'Europe',
        tagline: 'Birthplace of pizza, soul of Italy',
        price: 600,
        match: 90,
        categories: ['food'],
        moods: ['food', 'culture'],
        image: 'https://images.unsplash.com/photo-1516483638261-f4dbaf036963?w=800&q=80',
        bestMonths: 'Apr–Jun, Sep–Oct',
        flightTime: '~9h from NYC',
        knownFor: ['Pizza margherita', 'Pompeii', 'Amalfi gateway', 'Sfogliatella', 'Spaccanapoli'],
        highlights: [
            { title: 'Pizza Pilgrimage', description: 'Queue at Da Michele or Sorbillo for the world\'s best margherita.' },
            { title: 'Pompeii Day Trip', description: 'Walk the streets of a city frozen in volcanic ash since 79 AD.' },
            { title: 'Spaccanapoli Stroll', description: 'Naples\' chaotic, vibrant main street — cribs, coffee and street pizza.' },
        ],
        travelTips: [
            'Pizza costs €3–5 at the best places; don\'t overpay at tourist traps.',
            'Keep valuables secure — Naples is vibrant but can be chaotic.',
            'Use Naples as a base for Pompeii, Amalfi Coast and Capri day trips.',
        ],
    },
    {
        slug: 'copenhagen-food',
        name: 'Copenhagen',
        country: 'Denmark',
        region: 'Europe',
        tagline: 'New Nordic cuisine & hygge',
        price: 1100,
        match: 89,
        categories: ['food'],
        moods: ['food', 'culture'],
        image: 'https://images.unsplash.com/photo-1513622470522-26c3c8a854bc?w=800&q=80',
        bestMonths: 'May–Sep',
        flightTime: '~8h from NYC',
        knownFor: ['Noma legacy', 'Smørrebrød', 'Tivoli Gardens', 'Cycling culture', 'Design'],
        highlights: [
            { title: 'Torvehallerne Market', description: 'Covered food hall with smørrebrød, organic produce and specialty coffee.' },
            { title: 'Nyhavn Canal Walk', description: 'Colorful 17th-century waterfront — the postcard shot of Copenhagen.' },
            { title: 'Tivoli Gardens', description: 'World\'s second-oldest amusement park with rides, concerts and restaurants.' },
        ],
        travelTips: [
            'Copenhagen is very expensive; budget for $50–70/day on food.',
            'Rent a bicycle — the city is flat and has dedicated bike lanes everywhere.',
            'Many restaurants are closed Sunday–Monday; plan ahead.',
        ],
    },
    {
        slug: 'lyon-food',
        name: 'Lyon',
        country: 'France',
        region: 'Europe',
        tagline: 'France\'s gastronomic heart',
        price: 800,
        match: 88,
        categories: ['food'],
        moods: ['food', 'culture'],
        image: 'https://images.unsplash.com/photo-1524484485831-a92ffc0de03f?w=800&q=80',
        bestMonths: 'Apr–Oct',
        flightTime: '~8h from NYC',
        knownFor: ['Bouchons', 'Paul Bocuse legacy', 'Les Halles', 'Vieux Lyon', 'Beaujolais wine'],
        highlights: [
            { title: 'Bouchon Dinner', description: 'Traditional Lyonnaise taverns serving quenelles, andouillette and silk-worker cuisine.' },
            { title: 'Les Halles de Lyon', description: 'Gourmet indoor market — oysters, charcuterie, cheese and Beaujolais by the glass.' },
            { title: 'Vieux Lyon Traboules', description: 'Secret Renaissance passageways connecting courtyards through silk merchants\' buildings.' },
        ],
        travelTips: [
            'Lyon invented the "bouchon" — pick ones with the official certification plaque.',
            'Wednesday and Saturday are the best days for outdoor markets.',
            'Take the TGV from Paris — it\'s only 2 hours by fast train.',
        ],
    },

    // ═══════════════════════ BUDGET ═══════════════════════
    {
        slug: 'tbilisi-budget',
        name: 'Tbilisi',
        country: 'Georgia',
        region: 'Europe',
        tagline: 'Wine, warmth & ancient charm',
        price: 350,
        match: 90,
        categories: ['budget', 'culture'],
        moods: ['culture', 'food', 'adventure'],
        image: 'https://images.unsplash.com/photo-1565008576549-57569a49371d?w=800&q=80',
        bestMonths: 'May–Jun, Sep–Oct',
        flightTime: '~12h from NYC',
        knownFor: ['Sulfur baths', 'Natural wine', 'Khachapuri', 'Old Town', 'Narikala Fortress'],
        highlights: [
            { title: 'Abanotubani Sulfur Baths', description: 'Soak in centuries-old brick-domed thermal baths in the old town.' },
            { title: 'Wine Tasting in Kakheti', description: 'Day trip to Georgia\'s wine region — 8,000 years of winemaking in qvevri clay pots.' },
            { title: 'Narikala Fortress', description: 'Cable car to the ancient fortress overlooking the old city and Mtkvari River.' },
        ],
        travelTips: [
            'Georgia is incredibly affordable — $30/day covers food, wine and a guesthouse.',
            'Chacha (Georgian grappa) is offered freely everywhere; pace yourself.',
            'Marshrutka minibuses connect cities cheaply but schedules are flexible.',
        ],
    },
    {
        slug: 'budapest-budget',
        name: 'Budapest',
        country: 'Hungary',
        region: 'Europe',
        tagline: 'Thermal baths & ruin bars',
        price: 450,
        match: 91,
        categories: ['budget', 'culture', 'city'],
        moods: ['culture', 'nightlife', 'food'],
        image: 'https://images.unsplash.com/photo-1541849546-216549ae216d?w=800&q=80',
        bestMonths: 'Mar–May, Sep–Nov',
        flightTime: '~9h from NYC',
        knownFor: ['Széchenyi Baths', 'Ruin bars', 'Parliament building', 'Danube views', 'Goulash'],
        highlights: [
            { title: 'Széchenyi Thermal Bath', description: 'Europe\'s largest thermal bath complex — steam pools in a baroque palace.' },
            { title: 'Szimpla Kert Ruin Bar', description: 'The original ruin bar — eclectic décor, live music and cheap spritzers.' },
            { title: 'Fisherman\'s Bastion', description: 'Fairy-tale turrets with a sweeping view of Parliament across the Danube.' },
        ],
        travelTips: [
            'Buda is hilly and historic; Pest is flat with nightlife and food — explore both.',
            'Buy a 72-hour Budapest Card for transit and thermal bath entry.',
            'Avoid currency exchange places on Váci utca — rates are terrible.',
        ],
    },
    {
        slug: 'bucharest-budget',
        name: 'Bucharest',
        country: 'Romania',
        region: 'Europe',
        tagline: 'Paris of the East, reimagined',
        price: 320,
        match: 88,
        categories: ['budget'],
        moods: ['culture', 'nightlife', 'food'],
        image: 'https://images.unsplash.com/photo-1584646098378-0874589d76b1?w=800&q=80',
        bestMonths: 'Apr–Jun, Sep–Oct',
        flightTime: '~10h from NYC',
        knownFor: ['Palace of Parliament', 'Old Town nightlife', 'Day trips to Transylvania', 'Affordable dining'],
        highlights: [
            { title: 'Palace of Parliament Tour', description: 'The world\'s heaviest building — 1,100 rooms of communist-era excess.' },
            { title: 'Old Town Bar Crawl', description: 'Lipscani district packed with jazz bars, craft beer and live music.' },
            { title: 'Day Trip to Bran Castle', description: '2-hour drive to "Dracula\'s Castle" in the Carpathian mountains.' },
        ],
        travelTips: [
            'Bucharest is one of Europe\'s cheapest capitals — $25/day is comfortable.',
            'Use Bolt or Uber; licensed taxis can overcharge tourists.',
            'The metro is fast, clean and cheap — buy a contactless card at any station.',
        ],
    },
    {
        slug: 'ho-chi-minh-city-budget',
        name: 'Ho Chi Minh City',
        country: 'Vietnam',
        region: 'Asia',
        tagline: 'Pho, scooters & endless energy',
        price: 400,
        match: 89,
        categories: ['budget', 'food'],
        moods: ['food', 'culture', 'adventure'],
        image: 'https://images.unsplash.com/photo-1583417319070-4a69db38a482?w=800&q=80',
        bestMonths: 'Dec–Apr',
        flightTime: '~20h from NYC',
        knownFor: ['Street food', 'War Remnants Museum', 'Cu Chi Tunnels', 'Coffee culture', 'Scooter chaos'],
        highlights: [
            { title: 'Street Food Tour', description: 'Bánh mì, phở, bún bò Huế and egg coffee from sidewalk stalls.' },
            { title: 'Cu Chi Tunnels', description: 'Crawl through the vast tunnel network used during the Vietnam War.' },
            { title: 'Mekong Delta Day Trip', description: 'Floating markets, coconut candy workshops and sampan boat rides.' },
        ],
        travelTips: [
            'Crossing the street is an art — walk steadily and traffic flows around you.',
            'Vietnamese coffee with condensed milk (cà phê sữa đá) is addictive.',
            'Budget $20–30/day for a comfortable trip including food and sightseeing.',
        ],
    },
    {
        slug: 'sofia-budget',
        name: 'Sofia',
        country: 'Bulgaria',
        region: 'Europe',
        tagline: 'Mountain views on a shoestring',
        price: 300,
        match: 88,
        categories: ['budget'],
        moods: ['culture', 'nature', 'adventure'],
        image: 'https://images.unsplash.com/photo-1599230086142-2d68f6a1e85d?w=800&q=80',
        bestMonths: 'May–Sep',
        flightTime: '~10h from NYC',
        knownFor: ['Alexander Nevsky Cathedral', 'Vitosha Mountain', 'Roman ruins', 'Thermal springs', 'Affordable ski'],
        highlights: [
            { title: 'Alexander Nevsky Cathedral', description: 'Iconic gold-domed cathedral, one of the largest Orthodox churches in the world.' },
            { title: 'Vitosha Mountain Hike', description: 'Take the gondola or hike — alpine scenery just 30 minutes from the center.' },
            { title: 'Free Walking Tour', description: 'Tip-based tours covering Roman, Ottoman and communist history in 2 hours.' },
        ],
        travelTips: [
            'Sofia is one of Europe\'s cheapest capitals — expect to spend $20–25/day.',
            'Use the metro; it\'s fast, modern and covers the main sights.',
            'Bansko ski resort is 2 hours away with Europe\'s cheapest lift passes.',
        ],
    },
    {
        slug: 'krakow-budget',
        name: 'Kraków',
        country: 'Poland',
        region: 'Europe',
        tagline: 'Medieval heart, modern soul',
        price: 400,
        match: 90,
        categories: ['budget', 'culture'],
        moods: ['culture', 'food', 'nightlife'],
        image: 'https://images.unsplash.com/photo-1607078640017-583a83cd9aec?w=800&q=80',
        bestMonths: 'Apr–Jun, Sep–Oct',
        flightTime: '~9h from NYC',
        knownFor: ['Wawel Castle', 'Old Town square', 'Auschwitz day trip', 'Pierogi', 'Kazimierz nightlife'],
        highlights: [
            { title: 'Wawel Castle & Cathedral', description: 'Royal castle on a limestone hill overlooking the Vistula River.' },
            { title: 'Cloth Hall & Main Square', description: 'Europe\'s largest medieval town square, buzzing with life since the 13th century.' },
            { title: 'Kazimierz Quarter', description: 'Jewish heritage district turned food and nightlife hotspot.' },
        ],
        travelTips: [
            'Kraków is very walkable; the Old Town is car-free.',
            'Auschwitz-Birkenau is 1 hour away — book tickets online (free but timed).',
            'Try oscypek (smoked sheep cheese) from the market stalls.',
        ],
    },

    // ═══════════════════════ LUXURY ═══════════════════════
    {
        slug: 'dubai-luxury',
        name: 'Dubai',
        country: 'UAE',
        region: 'Asia',
        tagline: 'Towers, deserts & lavish nights',
        price: 1600,
        match: 93,
        categories: ['luxury', 'city', 'beach'],
        moods: ['beach', 'shopping', 'nightlife', 'adventure'],
        image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800&q=80',
        bestMonths: 'Oct–Apr',
        flightTime: '~13h from NYC',
        knownFor: ['Burj Khalifa', 'Desert safari', 'Dubai Mall', 'Palm Jumeirah', 'Gold Souk'],
        highlights: [
            { title: 'Burj Khalifa Observation', description: 'Sunset from the 148th floor of the world\'s tallest building.' },
            { title: 'Desert Safari & BBQ', description: 'Dune bashing, camel rides and a starlit BBQ dinner in the Arabian sands.' },
            { title: 'Atlantis Aquaventure', description: 'World-class waterpark and aquarium on the Palm Jumeirah.' },
        ],
        travelTips: [
            'Friday is the weekend; Thursday night is the big going-out night.',
            'The Dubai Metro covers the main strip — much cheaper than taxis.',
            'Dress modestly outside beach areas; it\'s a conservative culture.',
        ],
    },
    {
        slug: 'monaco-luxury',
        name: 'Monaco',
        country: 'Monaco',
        region: 'Europe',
        tagline: 'Superyachts, casinos & glamour',
        price: 2500,
        match: 90,
        categories: ['luxury'],
        moods: ['romance', 'nightlife'],
        image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&q=80',
        bestMonths: 'May–Sep',
        flightTime: '~8h from NYC to Nice + 30m',
        knownFor: ['Monte Carlo Casino', 'Grand Prix', 'Yacht harbor', 'Oceanographic Museum', 'Fine dining'],
        highlights: [
            { title: 'Monte Carlo Casino', description: 'James Bond glamour in the world\'s most famous casino.' },
            { title: 'Prince\'s Palace', description: 'Changing of the guard at the cliffside royal residence.' },
            { title: 'Port Hercules Yacht Walk', description: 'Stroll past hundreds of superyachts in the iconic harbor.' },
        ],
        travelTips: [
            'Stay in Nice and take the 20-minute train in — Monaco hotels are extremely expensive.',
            'The Grand Prix (late May) doubles prices but the atmosphere is electric.',
            'Monaco is tiny — you can walk the entire country in an afternoon.',
        ],
    },
    {
        slug: 'singapore-luxury',
        name: 'Singapore',
        country: 'Singapore',
        region: 'Asia',
        tagline: 'Garden city, infinite skyline',
        price: 1400,
        match: 92,
        categories: ['luxury', 'food', 'city'],
        moods: ['food', 'shopping', 'culture'],
        image: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=800&q=80',
        bestMonths: 'Feb–Apr',
        flightTime: '~19h from NYC',
        knownFor: ['Marina Bay Sands', 'Gardens by the Bay', 'Hawker centres', 'Orchard Road', 'Sentosa Island'],
        highlights: [
            { title: 'Gardens by the Bay', description: 'Supertree Grove light show and Cloud Forest dome — futuristic nature.' },
            { title: 'Hawker Centre Feast', description: 'Michelin-starred chicken rice for $3 at Chinatown Complex.' },
            { title: 'Marina Bay Sands Infinity Pool', description: 'Iconic rooftop pool overlooking the entire city skyline.' },
        ],
        travelTips: [
            'Hawker centres offer world-class food for $3–5 per dish.',
            'The MRT is fast, clean and covers everywhere — no need for taxis.',
            'Carry an umbrella; tropical downpours hit almost daily.',
        ],
    },
    {
        slug: 'st-barts-luxury',
        name: 'St. Barts',
        country: 'France (Overseas)',
        region: 'Latin America',
        tagline: 'Caribbean chic, French savoir-faire',
        price: 2800,
        match: 88,
        categories: ['luxury', 'beach'],
        moods: ['beach', 'romance'],
        image: 'https://images.unsplash.com/photo-1580237541049-2d715a09486e?w=800&q=80',
        bestMonths: 'Dec–May',
        flightTime: '~4h from Miami + puddle-jumper',
        knownFor: ['Saline Beach', 'French cuisine', 'Celebrity scene', 'Boutique hotels', 'Gustavia harbor'],
        highlights: [
            { title: 'Saline Beach', description: 'Pristine, uncrowded beach backed by salt ponds and low dunes.' },
            { title: 'Gustavia Harbor', description: 'Tiny capital with mega-yachts, French cafés and duty-free shopping.' },
            { title: 'Sunset at Nikki Beach', description: 'Champagne and lobster at the island\'s most glamorous beach club.' },
        ],
        travelTips: [
            'Fly via St. Maarten (SXM) then a 10-minute puddle-jumper.',
            'Everything is expensive — budget $200+/day for food alone.',
            'Low season (Jun–Nov) has significantly lower rates and fewer crowds.',
        ],
    },
    {
        slug: 'bora-bora-luxury',
        name: 'Bora Bora',
        country: 'French Polynesia',
        region: 'Oceania',
        tagline: 'Lagoon jewel of the Pacific',
        price: 3000,
        match: 92,
        categories: ['luxury', 'beach'],
        moods: ['beach', 'romance'],
        image: 'https://images.unsplash.com/photo-1589197331516-4d84b72ebde3?w=800&q=80',
        bestMonths: 'May–Oct',
        flightTime: '~12h from LAX',
        knownFor: ['Overwater bungalows', 'Mount Otemanu', 'Lagoon snorkeling', 'Shark feeding', 'Sunsets'],
        highlights: [
            { title: 'Overwater Bungalow', description: 'Glass-floor villa above waters so clear you can watch fish from bed.' },
            { title: 'Lagoon Snorkel Tour', description: 'Swim with rays, reef sharks and tropical fish in turquoise water.' },
            { title: 'Mount Otemanu Viewpoint', description: 'Boat to a sandbar for the iconic view of the volcanic peak.' },
        ],
        travelTips: [
            'Fly via Tahiti (PPT) with Air Tahiti; there are no direct flights from the US.',
            'Everything is flown in; expect to pay 3x mainland prices for food.',
            'Bring reef-safe sunscreen; the lagoon ecosystem is fragile.',
        ],
    },
    {
        slug: 'aspen-luxury',
        name: 'Aspen',
        country: 'USA',
        region: 'USA',
        tagline: 'Powder, champagne & mountain air',
        price: 2000,
        match: 89,
        categories: ['luxury', 'adventure'],
        moods: ['adventure', 'romance'],
        image: 'https://images.unsplash.com/photo-1578645510447-e20b4311e3ce?w=800&q=80',
        bestMonths: 'Dec–Mar (ski), Jun–Sep (summer)',
        flightTime: '~5h from NYC',
        knownFor: ['World-class skiing', 'Après-ski scene', 'Maroon Bells', 'Celebrity sightings', 'Food & Wine Festival'],
        highlights: [
            { title: 'Aspen Mountain Skiing', description: 'Four mountains, 5,300+ acres and legendary powder days.' },
            { title: 'Maroon Bells Hike', description: 'Colorado\'s most photographed peaks reflected in a pristine alpine lake.' },
            { title: 'Après-Ski at Ajax Tavern', description: 'Truffle fries and champagne on the sun-drenched deck at the base of Aspen Mountain.' },
        ],
        travelTips: [
            'Book accommodation far in advance — Aspen sells out in peak season.',
            'RFTA buses are free within Aspen; the town is very walkable.',
            'Summer is less crowded and has incredible hiking, biking and music festivals.',
        ],
    },
    {
        slug: 'zurich-luxury',
        name: 'Zurich',
        country: 'Switzerland',
        region: 'Europe',
        tagline: 'Lakeside elegance & Alpine views',
        price: 1800,
        match: 88,
        categories: ['luxury', 'city'],
        moods: ['culture', 'romance'],
        image: 'https://images.unsplash.com/photo-1515488764276-beab0607c1e6?w=800&q=80',
        bestMonths: 'Jun–Sep',
        flightTime: '~8h from NYC',
        knownFor: ['Lake Zurich', 'Bahnhofstrasse shopping', 'Chocolate', 'Old Town', 'Day trips to Alps'],
        highlights: [
            { title: 'Lake Zurich Swim', description: 'Locals swim in the crystal-clear lake — join them at one of many free bathing spots.' },
            { title: 'Old Town (Altstadt)', description: 'Medieval streets, guild houses and the double-towered Grossmünster.' },
            { title: 'Lindt Chocolate Factory', description: 'The world\'s largest Lindt shop with tastings and a chocolate fountain.' },
        ],
        travelTips: [
            'Zurich is one of the world\'s most expensive cities; eat at co-op restaurants to save.',
            'The ZürichCARD covers transit, museums and a boat trip on the lake.',
            'Day-trip to Lucerne or Uetliberg mountain for Alpine scenery.',
        ],
    },

    // ═══════════════════════ CITY BREAKS ═══════════════════════
    {
        slug: 'new-york-city-break',
        name: 'New York',
        country: 'USA',
        region: 'USA',
        tagline: 'Broadway, Brooklyn & beyond',
        price: 750,
        match: 93,
        categories: ['city', 'culture', 'food'],
        moods: ['culture', 'nightlife', 'food', 'shopping'],
        image: 'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?w=800&q=80',
        bestMonths: 'Apr–Jun, Sep–Nov',
        flightTime: 'Domestic hub',
        knownFor: ['Broadway', 'Central Park', 'Statue of Liberty', 'Brooklyn Bridge', 'Pizza & bagels'],
        highlights: [
            { title: 'Broadway Show', description: 'See a Tony-winning show in the Theater District — the world\'s stage capital.' },
            { title: 'Brooklyn Bridge Walk', description: 'Walk from Manhattan to DUMBO for skyline views and Grimaldi\'s pizza.' },
            { title: 'Central Park Morning', description: 'Rent a bike and ride through 843 acres of green in the heart of Manhattan.' },
        ],
        travelTips: [
            'Get a 7-day unlimited MetroCard — subway is the fastest way around.',
            'Times Square is for photos only; eat and drink literally anywhere else.',
            'TKTS booth in Times Square offers same-day Broadway tickets at 50% off.',
        ],
    },
    {
        slug: 'london-city-break',
        name: 'London',
        country: 'UK',
        region: 'Europe',
        tagline: 'Royals, pubs & infinite culture',
        price: 900,
        match: 91,
        categories: ['city', 'culture'],
        moods: ['culture', 'food', 'nightlife', 'shopping'],
        image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=800&q=80',
        bestMonths: 'Apr–Jun, Sep–Oct',
        flightTime: '~7h from NYC',
        knownFor: ['British Museum', 'West End theatre', 'Tower of London', 'Pubs', 'Borough Market'],
        highlights: [
            { title: 'West End Show', description: 'Catch a world-class musical or play in Theatreland.' },
            { title: 'Borough Market', description: 'London\'s oldest food market — Scotch eggs, raclette and craft gin.' },
            { title: 'Tower of London', description: 'Crown Jewels, ravens and 1,000 years of royal intrigue.' },
        ],
        travelTips: [
            'Use an Oyster card or contactless for the Tube — it\'s much cheaper than paper tickets.',
            'Most major museums (British Museum, National Gallery, Tate) are free.',
            'Book Afternoon Tea well in advance if that\'s on your list.',
        ],
    },
    {
        slug: 'berlin-city-break',
        name: 'Berlin',
        country: 'Germany',
        region: 'Europe',
        tagline: 'History, techno & street art',
        price: 650,
        match: 90,
        categories: ['city', 'budget'],
        moods: ['culture', 'nightlife', 'food'],
        image: 'https://images.unsplash.com/photo-1560969184-10fe8719e047?w=800&q=80',
        bestMonths: 'May–Sep',
        flightTime: '~9h from NYC',
        knownFor: ['Brandenburg Gate', 'Berlin Wall', 'Techno clubs', 'Döner kebab', 'Museum Island'],
        highlights: [
            { title: 'East Side Gallery', description: 'The longest surviving stretch of the Berlin Wall, now an open-air gallery.' },
            { title: 'Museum Island', description: 'Five world-class museums on an island in the Spree — UNESCO listed.' },
            { title: 'Berghain Nightclub', description: 'The world\'s most famous techno club — if you can get past the door.' },
        ],
        travelTips: [
            'Berlin is very affordable for a major European capital — €30/day is realistic.',
            'U-Bahn/S-Bahn is excellent; get a day pass for unlimited rides.',
            'Most clubs don\'t open until midnight and peak at 4am.',
        ],
    },
    {
        slug: 'amsterdam-city-break',
        name: 'Amsterdam',
        country: 'Netherlands',
        region: 'Europe',
        tagline: 'Canals, bikes & golden-age art',
        price: 800,
        match: 91,
        categories: ['city', 'culture'],
        moods: ['culture', 'nightlife', 'food'],
        image: 'https://images.unsplash.com/photo-1534351590666-13e3e96b5017?w=800&q=80',
        bestMonths: 'Apr–Jun, Sep',
        flightTime: '~8h from NYC',
        knownFor: ['Canals', 'Rijksmuseum', 'Anne Frank House', 'Cycling', 'Tulips'],
        highlights: [
            { title: 'Canal Boat Cruise', description: 'Glide through 17th-century canal rings past gabled merchant houses.' },
            { title: 'Rijksmuseum', description: 'Rembrandt\'s Night Watch and Vermeer\'s Milkmaid in a grand gallery.' },
            { title: 'Vondelpark Afternoon', description: 'Amsterdam\'s green lung — pack a picnic and people-watch.' },
        ],
        travelTips: [
            'Book Anne Frank House tickets exactly 6 weeks in advance — they sell out in minutes.',
            'Rent a bike and ride like a local; it\'s the best way to see the city.',
            'Avoid tourist traps near Leidseplein; the Jordaan has better food.',
        ],
    },
    {
        slug: 'seoul-city-break',
        name: 'Seoul',
        country: 'South Korea',
        region: 'Asia',
        tagline: 'K-culture, temples & street food',
        price: 900,
        match: 92,
        categories: ['city', 'food', 'culture'],
        moods: ['culture', 'food', 'nightlife', 'shopping'],
        image: 'https://images.unsplash.com/photo-1534274988757-a28bf1a57c17?w=800&q=80',
        bestMonths: 'Mar–May, Sep–Nov',
        flightTime: '~14h from NYC',
        knownFor: ['K-pop & K-drama', 'Gyeongbokgung', 'Myeongdong shopping', 'Korean BBQ', 'Hanbok rental'],
        highlights: [
            { title: 'Gyeongbokgung Palace', description: 'Rent a hanbok and explore the 600-year-old Joseon royal palace.' },
            { title: 'Hongdae Night Market', description: 'K-indie busking, street food and vintage shopping in Seoul\'s creative hub.' },
            { title: 'Namsan Tower Sunset', description: 'Cable car to the hilltop tower for panoramic city views and love locks.' },
        ],
        travelTips: [
            'Get a T-money card for seamless metro, bus and convenience store payments.',
            'Korean BBQ is best in groups; solo travelers should try jjigae (stew) restaurants.',
            'Download Papago or Naver Map — Google Maps doesn\'t work well in Korea.',
        ],
    },

    // ═══════════════════════ ROMANCE ═══════════════════════
    {
        slug: 'paris-romance',
        name: 'Paris',
        country: 'France',
        region: 'Europe',
        tagline: 'Art, bistros & the Eiffel Tower',
        price: 980,
        match: 95,
        categories: ['romance', 'culture', 'food', 'city'],
        moods: ['culture', 'romance', 'food', 'shopping'],
        image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800&q=80',
        bestMonths: 'Apr–Jun, Sep–Oct',
        flightTime: '~7h from NYC',
        knownFor: ['Eiffel Tower', 'Louvre', 'Montmartre', 'Seine cruises', 'Patisseries'],
        highlights: [
            { title: 'Eiffel Tower at Golden Hour', description: 'Champagne toast on the summit as Paris turns gold at sunset.' },
            { title: 'Seine River Dinner Cruise', description: 'Multi-course French dinner gliding past Notre-Dame and the Louvre.' },
            { title: 'Montmartre Morning', description: 'Climb to Sacré-Cœur, sketch in Place du Tertre and sip café crème.' },
        ],
        travelTips: [
            'Buy a Paris Museum Pass for skip-the-line access to 60+ museums.',
            'Avoid eating directly on the Champs-Élysées — quality is low, prices are high.',
            'The Marais and Saint-Germain have the best boutique shopping.',
        ],
    },
    {
        slug: 'venice-romance',
        name: 'Venice',
        country: 'Italy',
        region: 'Europe',
        tagline: 'Gondolas, masks & fading grandeur',
        price: 1100,
        match: 93,
        categories: ['romance', 'culture'],
        moods: ['romance', 'culture'],
        image: 'https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?w=800&q=80',
        bestMonths: 'Apr–Jun, Sep–Oct',
        flightTime: '~9h from NYC',
        knownFor: ['Grand Canal', 'St. Mark\'s Square', 'Gondola rides', 'Murano glass', 'Carnival'],
        highlights: [
            { title: 'Gondola at Sunset', description: 'Glide through narrow canals as the light turns the palazzos gold.' },
            { title: 'St. Mark\'s Basilica', description: 'Byzantine mosaics glittering under gold-leaf domes.' },
            { title: 'Murano Glass Workshop', description: 'Watch master artisans blow molten glass into works of art.' },
        ],
        travelTips: [
            'Stay near Rialto or Dorsoduro, not right on St. Mark\'s Square.',
            'Vaporetto (water bus) pass is more practical than gondolas for transport.',
            'Visit in shoulder season — summer is hot, crowded and acqua alta risks rise.',
        ],
    },
    {
        slug: 'provence-romance',
        name: 'Provence',
        country: 'France',
        region: 'Europe',
        tagline: 'Lavender fields & rosé sunsets',
        price: 1000,
        match: 91,
        categories: ['romance', 'food'],
        moods: ['romance', 'food', 'nature'],
        image: 'https://images.unsplash.com/photo-1499346030926-9a72daac6c63?w=800&q=80',
        bestMonths: 'Jun–Sep (lavender peaks Jul)',
        flightTime: '~8h from NYC to Marseille',
        knownFor: ['Lavender fields', 'Rosé wine', 'Provençal markets', 'Hilltop villages', 'Côte d\'Azur'],
        highlights: [
            { title: 'Lavender Fields of Valensole', description: 'Endless purple rows stretching to the horizon — peak bloom in July.' },
            { title: 'Wine Tasting in Luberon', description: 'Sip rosé at a family vineyard surrounded by olive groves and stone villages.' },
            { title: 'Gordes & Sénanque Abbey', description: 'Hilltop stone village overlooking lavender-lined abbey gardens.' },
        ],
        travelTips: [
            'Rent a car — Provence\'s villages and lavender fields require driving.',
            'Markets operate on different days in each village; plan your route accordingly.',
            'Lavender season is June–August; July is peak bloom for Valensole.',
        ],
    },
];

export default DESTINATIONS;
